import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Switch,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getAnswerVersions, rollbackAnswerVersion } from '../services/api';
import { Visibility as VisibilityIcon, History as HistoryIcon } from '@mui/icons-material';
import ReactDiffViewer from 'react-diff-viewer-continued';

const VersionDetailsDialog: React.FC<{ open: boolean; onClose: () => void; version: any }> = ({
  open,
  onClose,
  version,
}) => {
  if (!version) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Version {version.version_number} Details</DialogTitle>
      <DialogContent>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>View Content</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {version.answer_data?.answer_text}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const ComparisonResultDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  version1: any;
  version2: any;
}> = ({ open, onClose, version1, version2 }) => {
  if (!version1 || !version2) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Comparing Version {version1.version_number} and Version {version2.version_number}</DialogTitle>
      <DialogContent>
        <ReactDiffViewer
          oldValue={version1.answer_data?.answer_text}
          newValue={version2.answer_data?.answer_text}
          splitView={true}
          useDarkTheme={true}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const VersionHistory: React.FC = () => {
  const { answerId } = useParams<{ answerId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { questionText } = location.state || { questionText: 'Loading...' };

  const [versionHistory, setVersionHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [comparisonData, setComparisonData] = useState<{ version1: any; version2: any } | null>(null);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<any | null>(null);
  const [isVersionDetailsOpen, setIsVersionDetailsOpen] = useState(false);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!answerId) {
        setError('Answer ID is missing.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const history = await getAnswerVersions(answerId);
        setVersionHistory(history || []);
        setError(null);
      } catch (err) {
        setError('Failed to fetch version history.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [answerId]);
  
  const handleViewVersionDetails = (version: any) => {
    setSelectedVersion(version);
    setIsVersionDetailsOpen(true);
  };

  const handleCompareVersions = () => {
    if (selectedVersions.length !== 2) {
      // You can replace this with a more sophisticated notification system
      alert('Please select exactly two versions to compare.');
      return;
    }

    const version1 = versionHistory.find(v => v.id === selectedVersions[0]);
    const version2 = versionHistory.find(v => v.id === selectedVersions[1]);

    if (!version1 || !version2) {
      alert('Could not find selected versions.');
      return;
    }

    setComparisonData({ version1, version2 });
    setIsComparisonOpen(true);
  };

  const handleVersionSelection = (versionId: string) => {
    setSelectedVersions(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      }
      if (prev.length < 2) {
        return [...prev, versionId];
      }
      alert('You can only select up to two versions to compare.');
      return prev;
    });
  };

  const handleRollback = async (versionId: string) => {
    if (!answerId) return;
    try {
      await rollbackAnswerVersion(answerId, versionId);
      // You might want to refetch the history or navigate away
      // For now, let's just alert the user
      alert('Rollback successful!');
      navigate(-1); // Go back to the previous page
    } catch (error) {
      alert('Rollback failed.');
      console.error(error);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          Version History
        </Typography>
      </Box>
      <Typography variant="h6" gutterBottom>
        Question: {questionText}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom>Answer Versions</Typography>
        <Button
          variant="contained"
          onClick={handleCompareVersions}
          disabled={selectedVersions.length !== 2}
        >
          Compare Selected
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : versionHistory.length > 0 ? (
        <Paper variant="outlined">
          <List>
            {versionHistory.map((version, index) => (
              <React.Fragment key={version.id}>
                <ListItem>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Switch
                      checked={selectedVersions.includes(version.id)}
                      onChange={() => handleVersionSelection(version.id)}
                      size="small"
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">Version {version.version_number}</Typography>
                          {index === 0 && <Chip label="Current" color="primary" size="small" />}
                        </Box>
                      }
                      secondary={`Updated on ${new Date(version.created_at).toLocaleDateString()} by ${version.creator?.name || 'N/A'}`}
                    />
                  </Stack>
                  <Box sx={{ flexGrow: 1 }} />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton edge="end" aria-label="view" onClick={() => handleViewVersionDetails(version)}>
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="rollback"
                      onClick={() => handleRollback(version.id)}
                      disabled={index === 0} // Disable for the current version
                    >
                      <HistoryIcon />
                    </IconButton>
                  </Stack>
                </ListItem>
                {index < versionHistory.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      ) : (
        <Typography>No version history found.</Typography>
      )}

      {selectedVersion && (
        <VersionDetailsDialog
          open={isVersionDetailsOpen}
          onClose={() => setIsVersionDetailsOpen(false)}
          version={selectedVersion}
        />
      )}

      <ComparisonResultDialog
        open={isComparisonOpen}
        onClose={() => setIsComparisonOpen(false)}
        version1={comparisonData?.version1}
        version2={comparisonData?.version2}
      />
    </Box>
  );
};

export default VersionHistory;